import { convertFormattedValue, getItemLabel, isCustomDimension, isDimension, isField, isMetric, isNumericItem, isTableCalculation, itemsInMetricQuery, } from '@lightdash/common';
import { isEqual, uniq } from 'es-toolkit/compat';
import { resolvePivotRowFieldIds, shouldDisableMetricsAsRows, } from './pivotRows';
import { canHaveWarehouseTotal } from './totals';
/** The ids of every item the metric query selects, in query order. */
export const getTableSelectedItemIds = (metricQuery) => metricQuery ? itemsInMetricQuery(metricQuery) : undefined;
/**
 * Whether a table with no saved `showTableNames` should show them: it does as
 * soon as the items come from a table. (The reduce keeps the frontend's
 * historical `> 0` check, so one table is enough.)
 */
export const shouldDefaultShowTableNames = (itemsMap) => Object.values(itemsMap).reduce((acc, item) => {
    if (isField(item)) {
        acc.push(item.table);
    }
    return uniq(acc);
}, []).length > 0;
export const getTableFieldLabelDefault = (itemsMap, showTableNames, fieldId) => {
    if (!fieldId || !itemsMap || !(fieldId in itemsMap))
        return undefined;
    const item = itemsMap[fieldId];
    if (isField(item) && !showTableNames) {
        return item.label;
    }
    return getItemLabel(item);
};
export const getTableFieldLabelOverride = (columnProperties, fieldId) => fieldId ? columnProperties[fieldId]?.name : undefined;
export const getTableFieldLabel = (itemsMap, columnProperties, showTableNames, fieldId) => getTableFieldLabelOverride(columnProperties, fieldId) ||
    getTableFieldLabelDefault(itemsMap, showTableNames, fieldId);
export const isTableColumnVisible = (columnProperties, fieldId) => columnProperties[fieldId]?.visible ?? true;
export const isTableColumnFrozen = (columnProperties, fieldId) => columnProperties[fieldId]?.frozen === true;
export const getTableColumnWidth = (columnProperties, fieldId) => columnProperties[fieldId]?.width;
export const isPivotTableEnabled = (resultsData, pivotDimensions) => resultsData?.metricQuery &&
    resultsData.metricQuery.metrics.length > 0 &&
    resultsData.rows.length &&
    pivotDimensions &&
    pivotDimensions.length > 0;
// True when the configured pivot dimensions differ from the ones the current
// results were computed with (warehouse pivots key on groupByColumns). Mirrors
// the mismatch check in VisualizationWarning so a re-run is needed.
export const isPivotResultStale = (pivotDimensions, groupByColumns, isColumnVisible) => {
    const resultsPivotDimensions = groupByColumns?.map((col) => col.reference) ?? [];
    // Compare only VISIBLE configured dims — a hidden sort-only pivot dim is
    // routed to sortOnlyDimensions and never appears in results.groupByColumns,
    // so including it here would keep the re-run prompt permanently stale.
    const visiblePivotDimensions = (pivotDimensions ?? []).filter(isColumnVisible);
    return !isEqual(visiblePivotDimensions, resultsPivotDimensions);
};
/** The dimension (and custom dimension) ids of the column order. */
export const getTableDimensions = (columnOrder, itemsMap) => {
    if (!itemsMap)
        return [];
    return columnOrder.filter((fieldId) => {
        const item = itemsMap[fieldId];
        return item && (isDimension(item) || isCustomDimension(item));
    });
};
export const getNumUnpivotedDimensions = (dimensions, pivotDimensions) => dimensions.length - (pivotDimensions?.length || 0);
// Subtotals re-derive from the metric query behind the source query. A
// merge has no such query — its metric query describes the merged result
// rather than anything the warehouse can be asked to group again.
export const canUseTableSubtotals = (numUnpivotedDimensions, isMerged) => numUnpivotedDimensions > 1 && !isMerged;
// Once dimensions are loaded, turn off subtotals if there are not enough dimensions.
export const shouldDisableSubtotals = (dimensionsCount, numUnpivotedDimensions) => dimensionsCount > 0 && numUnpivotedDimensions < 2;
// A dimension-only table has nothing the warehouse can total; skip the
// request instead of letting the backend refuse it.
export const hasTotalableColumns = (columnOrder, itemsMap) => columnOrder.some((fieldId) => canHaveWarehouseTotal(itemsMap?.[fieldId]));
// Index dimension field ids the warehouse row-total query groups by — the
// worker keys each rendered row's total by these. Row totals are exclusively
// warehouse-computed (no client-side fallback) for SQL pivots, in both the
// metrics-as-columns and metrics-as-rows layouts.
export const getRowTotalIndexFieldIds = (indexColumn) => {
    if (!indexColumn)
        return [];
    return Array.isArray(indexColumn)
        ? indexColumn.map((col) => col.reference)
        : [indexColumn.reference];
};
/**
 * The input of the pivot conversion, or null when the results are not a
 * pivot. The frontend runs the conversion in a web worker; a headless caller
 * passes this to `convertSqlPivotedRowsToPivotData` directly.
 */
export const buildTablePivotInput = ({ pivotDimensions, rows, metricQuery, pivotDetails, selectedItemIds, getField, getFieldLabel, isColumnVisible, metricsAsRows, rowFieldIds, columnOrder, showColumnCalculation, showRowCalculation, groupedSubtotals, groupedRowSubtotals, warehouseRowTotals, warehouseColumnTotals, warehouseGrandTotals, parameters, }) => {
    if (!pivotDimensions ||
        pivotDimensions.length === 0 ||
        !metricQuery ||
        !rows ||
        rows.length === 0) {
        return null;
    }
    const hiddenMetricFieldIds = selectedItemIds?.filter((fieldId) => {
        const field = getField(fieldId);
        return (!isColumnVisible(fieldId) &&
            field &&
            ((isField(field) && isMetric(field)) || isTableCalculation(field)));
    });
    const hiddenDimensionFieldIds = selectedItemIds?.filter((fieldId) => {
        const field = getField(fieldId);
        if (!field || isColumnVisible(fieldId))
            return false;
        // Custom SQL dimensions are not `Field`s but still behave as dims
        // in the pivot (driving sort order via sortOnlyDimensions).
        return ((isField(field) && isDimension(field)) || isCustomDimension(field));
    });
    const pivotConfig = {
        pivotDimensions,
        metricsAsRows,
        rowFieldIds,
        columnOrder,
        hiddenMetricFieldIds,
        hiddenDimensionFieldIds,
        columnTotals: showColumnCalculation,
        rowTotals: showRowCalculation,
    };
    if (!pivotDetails) {
        return null;
    }
    return {
        rows,
        pivotDetails,
        pivotConfig,
        getField,
        getFieldLabel,
        groupedSubtotals,
        groupedRowSubtotals,
        warehouseRowTotals,
        warehouseColumnTotals,
        warehouseGrandTotals,
        parameters,
    };
};
// Remove columnProperties from map if the column has been removed from results
export const pruneTableColumnProperties = (columnProperties, selectedItemIds) => Object.keys(columnProperties).reduce((acc, field) => selectedItemIds.includes(field)
    ? {
        ...acc,
        [field]: columnProperties[field],
    }
    : acc, {});
// Step 1: Identify which fields need min/max calculation
export const getFieldsNeedingMinMax = (conditionalFormattings, columnProperties) => {
    const fieldsNeedingMinMax = new Set();
    conditionalFormattings?.forEach((config) => {
        if (config.target) {
            fieldsNeedingMinMax.add(config.target.fieldId);
        }
    });
    Object.entries(columnProperties).forEach(([fieldId, props]) => {
        if (props.displayStyle === 'bar') {
            fieldsNeedingMinMax.add(fieldId);
        }
    });
    return fieldsNeedingMinMax;
};
/**
 * The min and max of every numeric field a conditional format or bar display
 * reads, across the result rows (and across a field's pivot columns when the
 * results are a SQL pivot). Undefined when nothing needs one.
 */
export const calculateConditionalFormattingMinMaxMap = ({ fieldsNeedingMinMax, itemsMap, resultsData, isColumnVisible, }) => {
    // Step 3: Build field-to-columns mapping
    // For SQL pivots: Maps base field (e.g., "revenue") to pivot columns (e.g., ["revenue_bank", "revenue_paypal"])
    // For non-pivots: Direct 1:1 mapping (e.g., "revenue" → ["revenue"])
    const fieldColumnMapping = new Map();
    for (const fieldId of fieldsNeedingMinMax) {
        if (!isColumnVisible(fieldId))
            continue;
        const field = itemsMap[fieldId];
        if (!field || !isNumericItem(field))
            continue;
        if (!resultsData.pivotDetails) {
            fieldColumnMapping.set(fieldId, [fieldId]);
        }
        else {
            const pivotColumnNames = resultsData.pivotDetails.valuesColumns
                .filter((col) => col.referenceField === fieldId)
                .map((col) => col.pivotColumnName);
            if (pivotColumnNames.length > 0) {
                fieldColumnMapping.set(fieldId, pivotColumnNames);
            }
        }
    }
    if (fieldColumnMapping.size === 0) {
        return undefined;
    }
    // Step 4: Single-pass collection of all values
    const fieldValues = new Map();
    for (const fieldId of fieldColumnMapping.keys()) {
        fieldValues.set(fieldId, []);
    }
    for (const row of resultsData.rows) {
        for (const [fieldId, columnNames] of fieldColumnMapping.entries()) {
            const values = fieldValues.get(fieldId) ?? [];
            const field = itemsMap[fieldId];
            for (const columnName of columnNames) {
                const rawValue = row[columnName]?.value?.raw;
                if (rawValue !== undefined &&
                    rawValue !== null &&
                    rawValue !== '') {
                    const numValue = Number(rawValue);
                    if (!Number.isNaN(numValue)) {
                        values.push(convertFormattedValue(numValue, field));
                    }
                }
            }
            // Update the values for the field
            fieldValues.set(fieldId, values);
        }
    }
    // Step 5: Calculate min/max for each field
    const result = {};
    for (const [fieldId, values] of fieldValues.entries()) {
        if (values.length > 0) {
            result[fieldId] = {
                min: Math.min(...values),
                max: Math.max(...values),
            };
        }
    }
    return Object.keys(result).length > 0 ? result : undefined;
};
/** One-shot, cache-free version of the frontend's `minMaxMap` memo. */
export const getConditionalFormattingMinMaxMap = ({ itemsMap, resultsData, conditionalFormattings, columnProperties, }) => {
    if (!itemsMap || !resultsData || resultsData.rows.length === 0) {
        return undefined;
    }
    const fieldsNeedingMinMax = getFieldsNeedingMinMax(conditionalFormattings, columnProperties);
    if (fieldsNeedingMinMax.size === 0) {
        return undefined;
    }
    return calculateConditionalFormattingMinMaxMap({
        fieldsNeedingMinMax,
        itemsMap,
        resultsData,
        isColumnVisible: (fieldId) => isTableColumnVisible(columnProperties, fieldId),
    });
};
/**
 * The valid table config the editor hook settles on for a freshly mounted
 * chart: the saved config with the defaults and repairs its effects apply
 * once the items and results are known.
 */
export const resolveTableChartConfig = ({ chartConfig, resultsData, itemsMap, columnOrder, pivotDimensions, pivotRows, isMerged = false, }) => {
    const selectedItemIds = getTableSelectedItemIds(resultsData?.metricQuery);
    let showTableNames = chartConfig?.showTableNames ?? false;
    if (chartConfig?.showTableNames === undefined && itemsMap !== undefined) {
        if (shouldDefaultShowTableNames(itemsMap)) {
            showTableNames = true;
        }
    }
    let columnProperties = chartConfig?.columns === undefined ? {} : chartConfig?.columns;
    if (Object.keys(columnProperties).length > 0 && selectedItemIds) {
        const newColumnProperties = pruneTableColumnProperties(columnProperties, selectedItemIds);
        // only update if something changed, otherwise we get into an infinite loop
        if (Object.keys(columnProperties).length !==
            Object.keys(newColumnProperties).length) {
            columnProperties = newColumnProperties;
        }
    }
    const rowFieldIds = resolvePivotRowFieldIds({
        selectedItemIds,
        itemsMap,
        pivotDimensions,
        columnOrder,
        pivotRows,
    });
    const metricsAsRows = chartConfig?.metricsAsRows || false;
    const disableMetricsAsRows = shouldDisableMetricsAsRows({
        metricsAsRows,
        selectedItemIds: selectedItemIds?.filter((fieldId) => columnProperties[fieldId]?.visible !== false),
        rowFieldIds,
        itemsMap,
    });
    const effectiveMetricsAsRows = metricsAsRows && !disableMetricsAsRows;
    const dimensions = getTableDimensions(columnOrder, itemsMap);
    const numUnpivotedDimensions = getNumUnpivotedDimensions(dimensions, pivotDimensions);
    const canUseSubtotals = canUseTableSubtotals(numUnpivotedDimensions, isMerged);
    let showSubtotals = chartConfig?.showSubtotals ?? false;
    if (shouldDisableSubtotals(dimensions.length, numUnpivotedDimensions)) {
        showSubtotals = false;
    }
    const validConfig = {
        showColumnCalculation: !!chartConfig?.showColumnCalculation,
        showRowCalculation: !!chartConfig?.showRowCalculation,
        showTableNames,
        showResultsTotal: chartConfig?.showResultsTotal ?? false,
        showSubtotals,
        showSubtotalsExpanded: chartConfig?.showSubtotalsExpanded ?? false,
        showRowGrouping: chartConfig?.showRowGrouping ?? false,
        columns: columnProperties,
        hideRowNumbers: chartConfig?.hideRowNumbers === undefined
            ? false
            : chartConfig.hideRowNumbers,
        conditionalFormattings: chartConfig?.conditionalFormattings ?? [],
        metricsAsRows: effectiveMetricsAsRows,
        rowLimit: chartConfig?.rowLimit,
    };
    return {
        validConfig,
        selectedItemIds,
        rowFieldIds,
        dimensions,
        numUnpivotedDimensions,
        canUseSubtotals,
        hasTotalableColumns: hasTotalableColumns(columnOrder, itemsMap),
        isPivotTableEnabled: !!isPivotTableEnabled(resultsData, pivotDimensions),
        isPivotResultStale: isPivotResultStale(pivotDimensions, resultsData?.pivotDetails?.groupByColumns, (fieldId) => isTableColumnVisible(columnProperties, fieldId)),
    };
};
